import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

interface ThemeToggleProps {
  className?: string;
}

/**
 * Sleek segmented dark/light mode toggle.
 * - Pill container with a sliding lime indicator
 * - Moon = dark active, Sun = light active
 * - Instant theme swap with a subtle 300ms slide
 */
export const ThemeToggle = ({ className }: ThemeToggleProps) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      onClick={toggleTheme}
      className={cn(
        "relative inline-flex items-center p-1 w-[68px] h-9 rounded-full",
        "bg-foreground/5 border border-border hover:border-foreground/20",
        "transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
    >
      {/* Sliding lime indicator */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute top-1 left-1 w-7 h-7 rounded-full bg-primary",
          "shadow-[0_0_15px_hsl(var(--primary)/0.35)]",
          "transition-transform duration-300 ease-out",
          isDark ? "translate-x-0" : "translate-x-[32px]",
        )}
      />

      {/* Moon slot (dark) */}
      <span
        aria-hidden="true"
        className={cn(
          "relative z-10 flex items-center justify-center w-7 h-7 transition-colors duration-200",
          isDark ? "text-primary-foreground" : "text-muted-foreground",
        )}
      >
        <Moon className="w-4 h-4" strokeWidth={2.5} />
      </span>

      {/* Sun slot (light) */}
      <span
        aria-hidden="true"
        className={cn(
          "relative z-10 flex items-center justify-center w-7 h-7 ml-auto transition-colors duration-200",
          isDark ? "text-muted-foreground" : "text-primary-foreground",
        )}
      >
        <Sun className="w-[18px] h-[18px]" strokeWidth={2.5} />
      </span>
    </button>
  );
};

export default ThemeToggle;
